import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS } from "./data";
import { scrollToId } from "./hooks";

const NAV = [
  { id: "services", label: "// SERVICES" },
  { id: "projects", label: "// PROJECTS" },
  { id: "commercial", label: "// COMMERCIAL" },
  { id: "domestic", label: "// DOMESTIC" },
  { id: "territory", label: "// AREAS" },
  { id: "faq", label: "// FAQ" },
  { id: "contact", label: "// CONTACT" },
];

function LogoMark() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" className="text-[#00c2a8]" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" />
      <line x1="3" y1="21" x2="21" y2="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header className="sticky top-0 z-50 bg-[#111923]/95 backdrop-blur border-b border-[#213042]">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <button
          onClick={() => go("hero")}
          className="flex items-center gap-3 text-left"
          data-testid="header-logo"
          aria-label="Silicone & Mastic Specialists — back to top"
        >
          <LogoMark />
          <span>
            <span className="font-mono-code text-sm font-bold uppercase tracking-wider text-white block leading-tight">
              Silicone &amp; Mastic Specialists
            </span>
            <span className="font-mono-code text-[10px] text-[#7d96ac] block">
              Scotland &amp; NE England &bull; Dodds Bros
            </span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-5 font-mono-code text-xs text-[#a2b8cc]" aria-label="Primary">
          {NAV.map((item) => (
            <button
              key={item.id}
              onClick={() => go(item.id)}
              className="hover:text-[#00c2a8] transition-colors"
              data-testid={`nav-${item.id}`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={BUSINESS.phone1Href}
            className="hidden xl:flex items-center gap-2 font-mono-code text-xs text-[#a2b8cc] hover:text-[#00c2a8] transition-colors"
            data-testid="header-phone-link"
          >
            <Phone className="w-3.5 h-3.5" aria-hidden="true" />
            {BUSINESS.phone1}
          </a>
          <button
            onClick={() => go("contact")}
            className="font-mono-code bg-[#00c2a8] text-[#111923] font-bold text-xs px-4 py-2 rounded uppercase tracking-wider hover:bg-white transition-colors"
            data-testid="header-quote-btn"
          >
            Get Free Quote
          </button>
          <button
            className="lg:hidden text-white p-1"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="mobile-menu-btn"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <nav
          className="lg:hidden fixed inset-x-0 top-[64px] bottom-0 z-[60] bg-[#0b121a] border-t border-[#213042] px-6 py-8 flex flex-col gap-6 overflow-y-auto"
          aria-label="Mobile"
          data-testid="mobile-menu"
        >
          {NAV.map((item) => (
            <button
              key={item.id}
              onClick={() => go(item.id)}
              className="font-mono-code text-left text-lg uppercase tracking-wider text-white hover:text-[#00c2a8] transition-colors"
              data-testid={`mobile-nav-${item.id}`}
            >
              {item.label}
            </button>
          ))}
          <a href={BUSINESS.phone1Href} className="font-mono-code text-[#00c2a8] text-lg mt-4" data-testid="mobile-phone-link">
            Call {BUSINESS.phone1}
          </a>
        </nav>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#0b121a] text-[#7d96ac] py-12 border-t border-[#182330]" data-testid="footer">
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <LogoMark />
            <span className="font-mono-code text-sm font-bold uppercase tracking-wider text-white">
              Silicone &amp; Mastic Specialists
            </span>
          </div>
          <p className="text-xs leading-relaxed">
            Professional silicone sealing and mastic contractors serving Scotland and North East England.
            From domestic resealing to major commercial and industrial contracts.
          </p>
          <p className="text-xs mt-4 text-[#a2b8cc]">{BUSINESS.parent}</p>
        </div>
        <div className="font-mono-code text-xs space-y-3">
          <span className="text-[#00c2a8] uppercase tracking-widest block">// CONTACT</span>
          <a href={BUSINESS.phone1Href} className="block hover:text-white transition-colors" data-testid="footer-phone1">{BUSINESS.phone1}</a>
          <a href={BUSINESS.phone2Href} className="block hover:text-white transition-colors" data-testid="footer-phone2">{BUSINESS.phone2}</a>
          <a href={`mailto:${BUSINESS.email}`} className="block hover:text-white transition-colors break-all" data-testid="footer-email">{BUSINESS.email}</a>
          <a href={BUSINESS.whatsapp} target="_blank" rel="noopener noreferrer" className="block hover:text-white transition-colors" data-testid="footer-whatsapp">
            WhatsApp: {BUSINESS.phone1}
          </a>
        </div>
        <nav className="font-mono-code text-xs space-y-3" aria-label="Footer">
          <span className="text-[#00c2a8] uppercase tracking-widest block">// SITE</span>
          {NAV.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToId(item.id)}
              className="block hover:text-white transition-colors"
              data-testid={`footer-nav-${item.id}`}
            >
              {item.label.replace("// ", "")}
            </button>
          ))}
        </nav>
      </div>
      <div className="max-w-6xl mx-auto px-6 mt-10 pt-6 border-t border-[#182330] flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[11px] font-mono-code">
        <span>Silicone &amp; Mastic Specialists &bull; {BUSINESS.parent}</span>
        <span>Covering Scotland &amp; North East England — Durham to John o&apos; Groats</span>
      </div>
    </footer>
  );
}
